import { useEffect, useState } from 'react'
import { useMutation } from '@tanstack/react-query'
import { apiFetch } from '../lib/api'
import { type SyncResult, SyncResultList } from './sync-results'

// Deleting a customer, in the two GDPR steps the backend implements
// (src/backend/modules/admin/user-deletion.service.ts):
//   Deactivate — locked out, billing ends at term end, restorable for 30 days.
//   Erase      — everything personal deleted for good; invoices kept.

export const RESTORE_DAYS = 30

export type RefundOption = 'none' | 'prorate'
export const REFUND_LABELS: Record<RefundOption, string> = {
	none: 'No refund',
	prorate: 'Refund unused time',
}

// Applied when the subscriptions are cancelled for good, at erasure.
function RefundChoice({ value, onChange }: { value: RefundOption; onChange: (v: RefundOption) => void }) {
	const options: { value: RefundOption; hint: string }[] = [
		{ value: 'none', hint: 'Cancel the subscriptions and keep the payment.' },
		{
			value: 'prorate',
			hint: 'Chargebee refunds the unused part of the paid term to their payment method. Usually nothing is left on a monthly plan after 30 days.',
		},
	]
	return (
		<fieldset className="mt-4">
			<legend className="label">Refund when the subscriptions are cancelled</legend>
			<div className="grid gap-2">
				{options.map((o) => (
					<label
						key={o.value}
						className={`flex cursor-pointer gap-3 rounded-lg border p-3 text-sm ${value === o.value ? 'border-ink bg-canvas' : 'border-line'}`}
					>
						<input
							type="radio"
							name="refund"
							className="mt-0.5"
							checked={value === o.value}
							onChange={() => onChange(o.value)}
						/>
						<span>
							<span className="font-medium">{REFUND_LABELS[o.value]}</span>
							<span className="block text-xs text-ink-soft">{o.hint}</span>
						</span>
					</label>
				))}
			</div>
		</fieldset>
	)
}

interface ActionResponse {
	results: SyncResult[]
}

function useModal(onClose: () => void, busy: boolean) {
	useEffect(() => {
		const onKey = (e: KeyboardEvent) => e.key === 'Escape' && !busy && onClose()
		document.addEventListener('keydown', onKey)
		document.body.style.overflow = 'hidden'
		return () => {
			document.removeEventListener('keydown', onKey)
			document.body.style.overflow = ''
		}
	}, [onClose, busy])
}

function Shell({ children, onClose, busy }: { children: React.ReactNode; onClose: () => void; busy: boolean }) {
	useModal(onClose, busy)
	return (
		<div className="fixed inset-0 z-50 flex items-center justify-center p-4">
			<button type="button" aria-hidden tabIndex={-1} className="absolute inset-0 bg-black/40" onClick={() => !busy && onClose()} />
			<div role="dialog" aria-modal className="card relative z-10 max-h-full w-full max-w-lg overflow-y-auto p-5">
				{children}
			</div>
		</div>
	)
}

// Shown after any of the three actions: what happened in each system.
function ResultView({ title, results, onClose, footer }: { title: string; results: SyncResult[]; onClose: () => void; footer?: React.ReactNode }) {
	return (
		<>
			<h2 className="font-display text-lg font-semibold">{title}</h2>
			<SyncResultList results={results} />
			{results.some((r) => r.status === 'failed') && (
				<p className="mt-3 rounded-lg bg-red-50 px-3 py-2 text-sm text-red-700">
					At least one system was not updated. Check it and fix it there by hand.
				</p>
			)}
			{footer}
			<div className="mt-5 flex justify-end">
				<button type="button" className="btn-primary" onClick={onClose}>
					Done
				</button>
			</div>
		</>
	)
}

// The backend phrases its errors for a human, including where a deletion
// stopped and that nothing in CBB was touched yet.
function ErrorLine({ error }: { error: unknown }) {
	if (!error) return null
	return <p className="mt-4 rounded-lg bg-red-50 px-3 py-2 text-sm text-red-700">{(error as Error).message}</p>
}

// --- Step 1 ------------------------------------------------------------------------

export function DeactivateUserDialog({
	userId,
	email,
	runningInstances,
	billedSubscriptions,
	onClose,
	onDone,
}: {
	userId: string
	email: string
	runningInstances: number
	billedSubscriptions: number
	onClose: () => void
	onDone: () => void
}) {
	const [reason, setReason] = useState('')
	const [refund, setRefund] = useState<RefundOption>('none')
	const [results, setResults] = useState<SyncResult[] | null>(null)
	const deactivate = useMutation({
		mutationFn: () =>
			apiFetch<ActionResponse>(`/api/admin/users/${userId}/deactivate`, {
				method: 'POST',
				body: { reason: reason.trim() || undefined, refund },
			}),
		onSuccess: (data) => {
			onDone()
			setResults(data.results)
		},
	})

	return (
		<Shell onClose={onClose} busy={deactivate.isPending}>
			{results ? (
				<ResultView
					title="Account deactivated"
					results={results}
					onClose={onClose}
					footer={
						<p className="mt-3 text-sm text-ink-soft">
							It will be deleted permanently in {RESTORE_DAYS} days. Until then you can restore it from this page.
						</p>
					}
				/>
			) : (
				<form
					onSubmit={(e) => {
						e.preventDefault()
						deactivate.mutate()
					}}
				>
					<h2 className="font-display text-lg font-semibold">Delete {email}?</h2>
					<div className="mt-3 space-y-3 text-sm text-ink-soft">
						<div>
							<p className="font-medium text-ink">Now — can be undone for {RESTORE_DAYS} days</p>
							<ul className="mt-1 list-disc space-y-0.5 pl-5">
								<li>The customer is signed out and can no longer sign in</li>
								<li>
									{billedSubscriptions
										? `Billing stops: ${billedSubscriptions} subscription${billedSubscriptions === 1 ? '' : 's'} will not renew (no refund — they keep what they paid for)`
										: 'No Chargebee subscriptions to stop'}
								</li>
								<li>Marketing email to them is blocked in Brevo</li>
								<li>
									{runningInstances
										? `Their ${runningInstances} Nextcloud instance${runningInstances === 1 ? ' keeps' : 's keep'} running until the paid term ends`
										: 'No running Nextcloud instances'}
								</li>
							</ul>
						</div>
						<div>
							<p className="font-medium text-ink">After {RESTORE_DAYS} days — permanent</p>
							<ul className="mt-1 list-disc space-y-0.5 pl-5">
								<li>Nextcloud instances deleted with all their files</li>
								<li>Login, Brevo contact and personal details deleted</li>
								<li>Subscriptions cancelled — {REFUND_LABELS[refund].toLowerCase()}</li>
								<li>Invoices kept in Chargebee for the legal retention period</li>
							</ul>
						</div>
					</div>

					<RefundChoice value={refund} onChange={setRefund} />

					<label className="label mt-4" htmlFor="del-reason">
						Reason (optional, for the activity log)
					</label>
					<input
						id="del-reason"
						className="input"
						value={reason}
						maxLength={500}
						onChange={(e) => setReason(e.target.value)}
						placeholder="e.g. Customer requested deletion by email"
					/>

					<ErrorLine error={deactivate.error} />

					<div className="mt-5 flex justify-end gap-2">
						<button type="button" className="btn-secondary" onClick={onClose} disabled={deactivate.isPending}>
							Cancel
						</button>
						<button type="submit" className="btn-danger" disabled={deactivate.isPending}>
							{deactivate.isPending ? 'Deactivating…' : 'Deactivate account'}
						</button>
					</div>
				</form>
			)}
		</Shell>
	)
}

// --- Step 2, on demand -------------------------------------------------------------

export function EraseUserDialog({
	userId,
	email,
	defaultRefund,
	onClose,
	onDone,
}: {
	userId: string
	email: string
	defaultRefund: RefundOption
	onClose: () => void
	onDone: () => void
}) {
	const [typed, setTyped] = useState('')
	const [refund, setRefund] = useState<RefundOption>(defaultRefund)
	const [results, setResults] = useState<SyncResult[] | null>(null)
	const matches = typed.trim().toLowerCase() === email.toLowerCase()
	const erase = useMutation({
		mutationFn: () => apiFetch<ActionResponse>(`/api/admin/users/${userId}/erase`, { method: 'POST', body: { confirmEmail: typed, refund } }),
		onSuccess: (data) => {
			onDone()
			setResults(data.results)
		},
	})

	return (
		<Shell onClose={onClose} busy={erase.isPending}>
			{results ? (
				<ResultView title="Account deleted" results={results} onClose={onClose} />
			) : (
				<form
					onSubmit={(e) => {
						e.preventDefault()
						if (matches) erase.mutate()
					}}
				>
					<h2 className="font-display text-lg font-semibold">Delete permanently now?</h2>
					<p className="mt-2 text-sm text-ink-soft">
						Use this when the customer asked for their data to be erased straight away. This cannot be undone:
					</p>
					<ul className="mt-2 list-disc space-y-0.5 pl-5 text-sm text-ink-soft">
						<li>Chargebee subscriptions cancelled immediately — {REFUND_LABELS[refund].toLowerCase()} (invoices kept)</li>
						<li className="font-medium text-red-700">Nextcloud instances deleted with ALL their files</li>
						<li>Brevo contact and its history deleted</li>
						<li>Login, addresses and personal details deleted; their name and email removed from the activity log</li>
					</ul>

					<RefundChoice value={refund} onChange={setRefund} />

					<label className="label mt-4" htmlFor="erase-confirm">
						Type <span className="font-mono">{email}</span> to confirm
					</label>
					<input
						id="erase-confirm"
						className="input font-mono"
						value={typed}
						onChange={(e) => setTyped(e.target.value)}
						autoComplete="off"
						spellCheck={false}
					/>

					<ErrorLine error={erase.error} />

					<div className="mt-5 flex justify-end gap-2">
						<button type="button" className="btn-secondary" onClick={onClose} disabled={erase.isPending}>
							Cancel
						</button>
						<button type="submit" className="btn-danger" disabled={!matches || erase.isPending}>
							{erase.isPending ? 'Deleting…' : 'Delete permanently'}
						</button>
					</div>
				</form>
			)}
		</Shell>
	)
}

// --- Undo step 1 -------------------------------------------------------------------

export function RestoreUserDialog({
	userId,
	email,
	onClose,
	onDone,
}: {
	userId: string
	email: string
	onClose: () => void
	onDone: () => void
}) {
	const [results, setResults] = useState<SyncResult[] | null>(null)
	const restore = useMutation({
		mutationFn: () => apiFetch<ActionResponse>(`/api/admin/users/${userId}/restore`, { method: 'POST' }),
		onSuccess: (data) => {
			onDone()
			setResults(data.results)
		},
	})

	return (
		<Shell onClose={onClose} busy={restore.isPending}>
			{results ? (
				<ResultView title="Account restored" results={results} onClose={onClose} />
			) : (
				<>
					<h2 className="font-display text-lg font-semibold">Restore {email}?</h2>
					<p className="mt-2 text-sm text-ink-soft">
						The scheduled deletion is cancelled, the customer can sign in again, and the subscriptions that deactivation
						stopped will renew again. A subscription whose paid term already ended cannot be restored — the customer would
						need to buy a new plan.
					</p>
					<ErrorLine error={restore.error} />
					<div className="mt-5 flex justify-end gap-2">
						<button type="button" className="btn-secondary" onClick={onClose} disabled={restore.isPending}>
							Cancel
						</button>
						<button type="button" className="btn-primary" onClick={() => restore.mutate()} disabled={restore.isPending}>
							{restore.isPending ? 'Restoring…' : 'Restore account'}
						</button>
					</div>
				</>
			)}
		</Shell>
	)
}
